import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { IconButton, Typography } from '@mui/material';
import { Draggable } from 'react-beautiful-dnd';
import { useFormContext } from 'react-hook-form';
import { CustomFormulaValues } from 'src/components/Forms/CustomFormulaForm';
import { OperatorIcon } from 'src/components/Forms/CustomFormulaForm/FormulaCalculator/components/OperatorIcon';
import {
  Chip,
  ChipLabelContainer,
  Count,
} from 'src/components/Forms/CustomFormulaForm/FormulaCalculator/components/styled';
import { ReactComponent as GrabIcon } from 'src/icons/grab.svg';
import { countFilledFilters, FormattedCustomFormulaItem } from 'src/utils';
import { CustomFormulaItemType } from 'src/utils/gql/types';

interface ItemProps {
  icon: ReactNode;
  index: number;
  item: FormattedCustomFormulaItem;
}

export const Item: FC<ItemProps> = ({ item, index, icon }) => {
  const chipRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { setValue, getValues, trigger } =
    useFormContext<CustomFormulaValues>();

  const { value, id, label, type, filters } = item;
  const count = countFilledFilters(filters || []);

  const handleDelete = () => {
    const items = getValues('formulaItems').filter((item) => item.id !== id);

    setValue('formulaItems', items, { shouldValidate: true });
    trigger('formulaItems');
  };

  useEffect(() => {
    const setWidth = () => {
      if (chipRef.current) {
        const styles = window.getComputedStyle(chipRef.current);
        const width = chipRef.current.offsetWidth;
        const marginLeft = parseFloat(styles.marginLeft);
        const marginRight = parseFloat(styles.marginRight);
        const totalWidth = width + marginLeft + marginRight;

        const items = getValues('formulaItems').map((item) => {
          return item.value === value ? { ...item, width: totalWidth } : item;
        });

        setValue('formulaItems', items, { shouldValidate: true });
      }
    };

    setWidth();
  }, [getValues, setValue, value, count]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <Chip
          ref={(node) => {
            chipRef.current = node;
            provided.innerRef(node);
          }}
          {...provided.draggableProps}
          icon={
            <IconButton {...provided.dragHandleProps}>
              <GrabIcon />
            </IconButton>
          }
          label={
            <ChipLabelContainer>
              {icon}
              {type === CustomFormulaItemType.metric ? (
                <>
                  <Typography>{label}</Typography>
                  {!!count && <Count>{count}</Count>}
                </>
              ) : type === CustomFormulaItemType.constant ? (
                <Typography>{label}</Typography>
              ) : (
                <OperatorIcon operator={label} />
              )}
            </ChipLabelContainer>
          }
          onDelete={handleDelete}
          variant="outlined"
          visible={+isVisible}
        />
      )}
    </Draggable>
  );
};
// test
